<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useSidebar } from 'dashboard/composables/useSidebar';
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from 'reka-ui';
import Icon from 'next/icon/Icon.vue';
import ButtonNext from 'next/button/Button.vue';

const emit = defineEmits(['showCreateAccountModal']);

const { t } = useI18n();
const store = useStore();
const { accountId, currentAccount } = useAccount();
const { isMobile } = useSidebar();
const currentUser = useMapGetter('getCurrentUser');
const globalConfig = useMapGetter('globalConfig/get');

const userAccounts = useMapGetter('getUserAccounts');

const showAccountSwitcher = computed(
  () => userAccounts.value.length > 1 && currentAccount.value.name
);

const sortedCurrentUserAccounts = computed(() => {
  return [...(currentUser.value.accounts || [])].sort((a, b) =>
    a.name.localeCompare(b.name)
  );
});

const onChangeAccount = newId => {
  const accountUrl = `/app/accounts/${newId}/dashboard`;
  window.location.href = accountUrl;
};

const emitNewAccount = () => {
  emit('showCreateAccountModal');
};
</script>

<template>
  <DropdownMenuRoot>
    <DropdownMenuTrigger as-child>
      <button
        id="sidebar-account-switcher"
        :data-account-id="accountId"
        aria-haspopup="listbox"
        aria-controls="account-options"
        class="flex items-center gap-2 justify-between w-full rounded-lg px-2"
        :class="[
          showAccountSwitcher
            ? 'hover:bg-n-alpha-1 cursor-pointer'
            : 'cursor-default',
        ]"
        :disabled="!showAccountSwitcher"
      >
        <span
          class="text-sm font-medium leading-5 text-n-slate-12 truncate"
          aria-live="polite"
        >
          {{ currentAccount.name }}
        </span>

        <Icon
          v-if="showAccountSwitcher"
          icon="i-lucide-chevron-down"
          class="size-4 text-n-slate-10 flex-shrink-0"
          aria-hidden="true"
        />
      </button>
    </DropdownMenuTrigger>

    <DropdownMenuPortal v-if="showAccountSwitcher">
      <DropdownMenuContent
        :side="isMobile ? 'bottom' : 'right'"
        align="start"
        :side-offset="4"
        class="min-w-80 rounded-lg bg-n-solid-1 border border-n-weak p-1.5 shadow-lg z-[9999]"
      >
        <DropdownMenuLabel
          class="px-2.5 py-2 text-[13px] font-medium text-n-slate-11"
        >
          {{ t('SIDEBAR_ITEMS.SWITCH_ACCOUNT') }}
        </DropdownMenuLabel>
        <DropdownMenuSeparator class="my-1.5 h-px bg-n-weak" />

        <DropdownMenuItem
          v-for="account in sortedCurrentUserAccounts"
          :key="account.id"
          class="flex items-center gap-2.5 rounded-md px-2.5 py-2 text-[15px] cursor-pointer outline-none hover:bg-n-alpha-2 hover:text-n-slate-12"
          :class="{
            'bg-n-blue-3/60 text-n-blue-11 font-medium':
              account.id === accountId,
          }"
          @click="onChangeAccount(account.id)"
        >
          <div class="flex items-center gap-2.5 flex-1 min-w-0">
            <span
              class="text-n-slate-12 max-w-36 truncate min-w-0"
              :title="account.name"
            >
              {{ account.name }}
            </span>
            <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
            <span
              class="text-n-slate-11 max-w-24 truncate capitalize"
              :title="account.name"
            >
              {{
                account.custom_role_id ? account.custom_role.name : account.role
              }}
            </span>
          </div>
          <Icon
            v-if="account.id === accountId"
            icon="i-lucide-check"
            class="text-n-teal-11 size-[20px] flex-shrink-0"
          />
        </DropdownMenuItem>

        <DropdownMenuSeparator
          v-if="globalConfig.createNewAccountFromDashboard"
          class="my-1.5 h-px bg-n-weak"
        />

        <DropdownMenuItem
          v-if="globalConfig.createNewAccountFromDashboard"
          class="p-0"
          @click.stop
        >
          <ButtonNext
            color="slate"
            variant="faded"
            class="w-full"
            size="sm"
            @click="emitNewAccount"
          >
            {{ t('CREATE_ACCOUNT.NEW_ACCOUNT') }}
          </ButtonNext>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenuPortal>
  </DropdownMenuRoot>
</template>
